import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Filter, Users, X, Zap } from 'lucide-react';

export default function AthleteSearchFilter({
  searchQuery,
  setSearchQuery,
  filterTeam,
  setFilterTeam,
  filterCoach,
  setFilterCoach,
  teams,
  coaches,
  filteredCount,
  totalCount
}) {
  const hasActiveFilters = searchQuery || filterTeam || filterCoach;

  const clearFilters = () => {
    setSearchQuery('');
    setFilterTeam('');
    setFilterCoach('');
  };

  return (
    <div className="mb-8">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#02b5ff] to-[#ff025f] rounded-xl flex items-center justify-center">
              <Zap className="h-5 w-5 text-white" />
            </div>
            Your Athletes
          </h2>
          <p className="text-zinc-400">Track growth, development, and athletic performance</p>
        </div>
        
        {/* Quick Stats */}
        <div className="flex gap-4">
          <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-2xl px-5 py-3 text-center">
            <div className="text-2xl font-bold text-[#02b5ff]">{totalCount}</div>
            <div className="text-xs text-zinc-500">Total Athletes</div>
          </div>
          <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-2xl px-5 py-3 text-center">
            <div className="text-2xl font-bold text-[#ff025f]">{teams.length}</div>
            <div className="text-xs text-zinc-500">Teams</div>
          </div>
          <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-2xl px-5 py-3 text-center">
            <div className="text-2xl font-bold text-green-500">{coaches.length}</div>
            <div className="text-xs text-zinc-500">Coaches</div>
          </div>
        </div>
      </div>
      
      {/* Search and Filter Bar */}
      <div className="bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 rounded-2xl p-4" data-testid="search-filter-bar">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
            <Input
              type="text"
              placeholder="Search athletes by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 bg-zinc-800/50 border-zinc-700 text-white placeholder-zinc-500 rounded-xl focus:border-[#02b5ff] focus:ring-[#02b5ff]/20"
              data-testid="athlete-search-input"
            />
          </div>
          
          {/* Team Filter */}
          <div className="relative min-w-[180px]">
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <select
              value={filterTeam}
              onChange={(e) => setFilterTeam(e.target.value)}
              className="w-full h-12 pl-11 pr-4 bg-zinc-800/50 border border-zinc-700 text-white rounded-xl appearance-none cursor-pointer focus:border-[#02b5ff] focus:outline-none focus:ring-2 focus:ring-[#02b5ff]/20"
              data-testid="team-filter"
            >
              <option value="">All Teams</option>
              {teams.map(team => (
                <option key={team} value={team}>{team}</option>
              ))}
            </select>
          </div>
          
          {/* Coach Filter */}
          <div className="relative min-w-[180px]">
            <Users className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <select
              value={filterCoach}
              onChange={(e) => setFilterCoach(e.target.value)}
              className="w-full h-12 pl-11 pr-4 bg-zinc-800/50 border border-zinc-700 text-white rounded-xl appearance-none cursor-pointer focus:border-[#02b5ff] focus:outline-none focus:ring-2 focus:ring-[#02b5ff]/20"
              data-testid="coach-filter"
            >
              <option value="">All Coaches</option>
              {coaches.map(coach => (
                <option key={coach} value={coach}>{coach}</option>
              ))}
            </select>
          </div>
          
          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              onClick={clearFilters}
              variant="outline"
              className="h-12 px-4 border-[#ff025f]/50 text-[#ff025f] hover:bg-[#ff025f]/10 rounded-xl"
              data-testid="clear-filters-btn"
            >
              <X className="h-4 w-4 mr-2" />
              Clear
            </Button>
          )}
        </div>
        
        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-800">
            <span className="text-xs text-zinc-500">Showing:</span>
            <span className="text-sm text-[#02b5ff] font-medium">{filteredCount} athlete{filteredCount !== 1 ? 's' : ''}</span>
            {searchQuery && (
              <span className="text-xs bg-[#02b5ff]/20 text-[#02b5ff] px-2 py-1 rounded-full">
                "{searchQuery}"
              </span>
            )}
            {filterTeam && (
              <span className="text-xs bg-[#ff025f]/20 text-[#ff025f] px-2 py-1 rounded-full">
                {filterTeam}
              </span>
            )}
            {filterCoach && (
              <span className="text-xs bg-green-500/20 text-green-500 px-2 py-1 rounded-full">
                {filterCoach}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
